import axios from 'axios';
import i18next from 'i18next';
import Config from 'Config';
import { createSignedRequest } from './createSignedRequest';
import { createSignedPostRequest } from './createSignedRequestsHelpers';

// Browser push: lets a chat message, a call or a project email reach this
// browser while no Lixani tab is open. The browser holds a subscription with
// its vendor's push service; the API stores it (PushSubscriptions) and the
// web-push lambda posts to it. See sw-template.js for the receiving end.
//
// Off when REACT_APP_VAPID_PUBLIC_KEY is unset, which is how an environment
// whose API has no web-push lambda yet keeps this switched off.

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// The Settings switch, per browser. Absent means on.
const DISABLED_KEY = 'pushNotificationsDisabled';

let active = false;

export const isPushSupported = () =>
  !!VAPID_PUBLIC_KEY &&
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/**
 * Whether this browser currently receives pushes. While it does, the push is
 * the notification for chat, calls and email when the tab is not focused, and
 * the tab's own notifications step aside (see browserNotifications.js).
 */
export const isPushActive = () => active;

export const isPushDisabledHere = () => {
  try {
    return localStorage.getItem(DISABLED_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const setDisabledHere = (disabled) => {
  try {
    if (disabled) localStorage.setItem(DISABLED_KEY, '1');
    else localStorage.removeItem(DISABLED_KEY);
  } catch (error) {
    // Private mode: the switch lasts for this session only.
  }
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

const sameKey = (buffer, base64String) => {
  if (!buffer) return false;
  const expected = urlBase64ToUint8Array(base64String);
  const actual = new Uint8Array(buffer);
  return (
    actual.length === expected.length &&
    actual.every((byte, index) => byte === expected[index])
  );
};

// The service worker registers only in production builds; without one there
// is nothing to subscribe with.
const getRegistration = async () => {
  const registration = await navigator.serviceWorker.getRegistration();
  return registration && registration.active ? registration : null;
};

const putSubscription = async (subscription) => {
  const apiPath = 'push-subscriptions';
  const { endpoint, keys } = subscription.toJSON();
  const body = {
    endpoint,
    keys,
    userAgent: navigator.userAgent,
    language: i18next.language,
  };

  const signedRequest = await createSignedRequest({
    url: `https://${process.env.REACT_APP_WORK_CALENDAR_API_URL}/${apiPath}`,
    host: process.env.REACT_APP_WORK_CALENDAR_API_URL,
    path: apiPath,
    method: 'PUT',
    region: Config.AWS_REGION,
    service: 'execute-api',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body), // aws4 signs body; axios sends data
    data: body,
  });

  await axios(signedRequest);
};

// The sync in flight. App start and a fresh permission grant can both ask at
// once; sharing one attempt keeps them from subscribing twice.
let syncing = null;

/**
 * Subscribe this browser, or refresh its subscription, and tell the API.
 * Safe to call any time: does nothing without permission, a service worker,
 * a configured key, or when switched off here. Called on every app start, so
 * a subscription the browser rotated is picked up within a session.
 */
export const syncPushSubscription = () => {
  if (!syncing) {
    syncing = sync().finally(() => {
      syncing = null;
    });
  }
  return syncing;
};

const sync = async () => {
  active = false;

  if (
    !isPushSupported() ||
    Notification.permission !== 'granted' ||
    isPushDisabledHere()
  ) {
    return false;
  }

  try {
    const registration = await getRegistration();
    if (!registration) return false;

    let subscription = await registration.pushManager.getSubscription();

    // Made with another key (a rotated one): it can never be pushed to again.
    if (
      subscription &&
      !sameKey(subscription.options.applicationServerKey, VAPID_PUBLIC_KEY)
    ) {
      await subscription.unsubscribe();
      subscription = null;
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    await putSubscription(subscription);
    active = true;
    return true;
  } catch (error) {
    console.warn('Push subscription failed:', error);
    return false;
  }
};

/**
 * Stop pushes to this browser for the signed-in user: on sign-out, so a
 * shared computer stops receiving their chats, and from the Settings switch.
 */
export const removePushSubscription = async ({ disableHere = false } = {}) => {
  active = false;
  if (disableHere) setDisabledHere(true);

  if (!isPushSupported()) return;

  try {
    const registration = await getRegistration();
    const subscription =
      registration && (await registration.pushManager.getSubscription());
    if (!subscription) return;

    const signedRequest = await createSignedPostRequest({
      apiPath: 'push-subscriptions/remove',
      body: { endpoint: subscription.endpoint },
    });
    await axios(signedRequest);
    await subscription.unsubscribe();
  } catch (error) {
    console.warn('Removing the push subscription failed:', error);
  }
};

/** The Settings switch turned back on. */
export const enablePushHere = async () => {
  setDisabledHere(false);
  return syncPushSubscription();
};
